/**
 * Multiple-comparison adjustments of p-values.
 *
 * The methods named in R's `stats::p.adjust` are ported from its source so the results
 * agree to rounding error, including its `n` argument (a family larger than the list of
 * p-values you hold, the unlisted p-values being taken as 1):
 *
 * - `bonferroni`: min(1, m·p).
 * - `holm` (Holm, 1979, step-down): with p sorted ascending, max over j ≤ i of
 *   min(1, (m − j + 1)·p₍ⱼ₎).
 * - `hochberg` (Hochberg, 1988, step-up): min over j ≥ i of min(1, (m − j + 1)·p₍ⱼ₎).
 * - `hommel` (Hommel, 1988).
 * - `BH` (Benjamini & Hochberg, 1995, false discovery rate): min over j ≥ i of
 *   min(1, m·p₍ⱼ₎ ÷ j).
 * - `BY` (Benjamini & Yekutieli, 2001): BH multiplied by Σ₁ᵐ 1/j, valid under any
 *   dependence.
 * Two more that `p.adjust` does not offer but statistics packages and GraphPad do:
 * - `sidak` (Šidák, 1967, single-step): 1 − (1 − p)^m, exact for independent tests.
 * - `holmSidak` (step-down Šidák): max over j ≤ i of 1 − (1 − p₍ⱼ₎)^(m − j + 1).
 */

export type AdjustMethod =
  | "bonferroni"
  | "sidak"
  | "holm"
  | "holmSidak"
  | "hochberg"
  | "hommel"
  | "bh"
  | "by";

export const ADJUST_METHODS: readonly AdjustMethod[] = [
  "bonferroni",
  "sidak",
  "holm",
  "holmSidak",
  "hochberg",
  "hommel",
  "bh",
  "by",
];

export type ErrorRate = "fwer" | "fdr";

export function errorRate(method: AdjustMethod): ErrorRate {
  return method === "bh" || method === "by" ? "fdr" : "fwer";
}

function cap(value: number): number {
  return Math.min(1, value);
}

/** Indices that sort `values` ascending (stable, like R's `order`). */
function orderOf(values: readonly number[], descending = false): number[] {
  const index = values.map((_, i) => i);
  index.sort((a, b) => {
    const diff = descending ? values[b] - values[a] : values[a] - values[b];
    return diff === 0 ? a - b : diff;
  });
  return index;
}

/** Apply a transform in sorted order and put the results back in the input order. */
function inOrder(
  p: readonly number[],
  descending: boolean,
  transform: (sortedP: number[]) => number[]
): number[] {
  const order = orderOf(p, descending);
  const adjusted = transform(order.map((i) => p[i]));
  const result = new Array<number>(p.length);
  for (const [position, original] of order.entries()) {
    result[original] = adjusted[position];
  }
  return result;
}

function cumulative(
  values: readonly number[],
  pick: (a: number, b: number) => number
): number[] {
  const out: number[] = [];
  for (const [index, value] of values.entries()) {
    out.push(index === 0 ? value : pick(out[index - 1], value));
  }
  return out;
}

function stepDown(
  p: readonly number[],
  m: number,
  scale: (value: number, remaining: number) => number
): number[] {
  return inOrder(p, false, (sortedP) =>
    cumulative(
      sortedP.map((value, i) => scale(value, m - i)),
      Math.max
    )
  );
}

function stepUp(
  p: readonly number[],
  scale: (value: number, rank: number) => number
): number[] {
  const lp = p.length;
  return inOrder(p, true, (sortedP) =>
    cumulative(
      sortedP.map((value, i) => scale(value, lp - i)),
      Math.min
    )
  );
}

function hommel(p: readonly number[], n: number): number[] {
  const lp = p.length;
  const padded = [...p, ...new Array<number>(Math.max(0, n - lp)).fill(1)];
  const size = padded.length;
  const order = orderOf(padded);
  const sortedP = order.map((i) => padded[i]);
  const minRatio = Math.min(
    ...sortedP.map((value, i) => (size * value) / (i + 1))
  );
  const q = new Array<number>(size).fill(minRatio);
  let pa = new Array<number>(size).fill(minRatio);
  for (let m = size - 1; m >= 2; m--) {
    const lowerCount = size - m + 1;
    const upper = sortedP.slice(size - m + 1);
    const q1 = Math.min(...upper.map((value, k) => (m * value) / (k + 2)));
    for (let k = 0; k < lowerCount; k++) {
      q[k] = Math.min(m * sortedP[k], q1);
    }
    for (let k = size - m + 1; k < size; k++) {
      q[k] = q[size - m];
    }
    pa = pa.map((value, k) => Math.max(value, q[k]));
  }
  const adjustedSorted = pa.map((value, k) => Math.max(value, sortedP[k]));
  const result = new Array<number>(size);
  for (const [position, original] of order.entries()) {
    result[original] = adjustedSorted[position];
  }
  return result.slice(0, lp);
}

/**
 * Adjusted p-values in the order given. `n` is the family size (default: the number of
 * p-values); an `n` larger than the list behaves as in R.
 */
export function adjustPValues(
  p: readonly number[],
  method: AdjustMethod,
  n: number = p.length
): number[] {
  const m = Math.max(n, p.length);
  if (p.length === 0) {
    return [];
  }
  switch (method) {
    case "bonferroni":
      return p.map((value) => cap(m * value));
    case "sidak":
      return p.map((value) => 1 - (1 - value) ** m);
    case "holm":
      return stepDown(p, m, (value, remaining) => cap(remaining * value));
    case "holmSidak":
      return stepDown(p, m, (value, remaining) => 1 - (1 - value) ** remaining);
    case "hochberg":
      return stepUp(p, (value, rank) => cap((m - rank + 1) * value));
    case "bh":
      return stepUp(p, (value, rank) => cap((m / rank) * value));
    case "by": {
      let harmonic = 0;
      for (let j = 1; j <= m; j++) {
        harmonic += 1 / j;
      }
      return stepUp(p, (value, rank) => cap(((harmonic * m) / rank) * value));
    }
    case "hommel":
      return hommel(p, m);
    default:
      return [...p];
  }
}

export interface AdjustedRow {
  index: number;
  label: string;
  p: number;
  adjusted: Record<AdjustMethod, number>;
}

export interface AdjustReport {
  rows: AdjustedRow[];
  m: number;
  alpha: number;
  /** How many hypotheses each method rejects at `alpha`. */
  rejected: Record<AdjustMethod, number>;
}

export function adjustReport(
  entries: readonly { label: string; p: number }[],
  alpha: number,
  n?: number
): AdjustReport {
  const p = entries.map((entry) => entry.p);
  const m = Math.max(n ?? p.length, p.length);
  const adjusted = Object.fromEntries(
    ADJUST_METHODS.map((method) => [method, adjustPValues(p, method, m)])
  ) as Record<AdjustMethod, number[]>;
  const rows = entries.map((entry, index) => ({
    index,
    label: entry.label,
    p: entry.p,
    adjusted: Object.fromEntries(
      ADJUST_METHODS.map((method) => [method, adjusted[method][index]])
    ) as Record<AdjustMethod, number>,
  }));
  const rejected = Object.fromEntries(
    ADJUST_METHODS.map((method) => [
      method,
      adjusted[method].filter((value) => value <= alpha).length,
    ])
  ) as Record<AdjustMethod, number>;
  return { rows, m, alpha, rejected };
}

/* ------------------------------------------------------------------ reading */

const LINE_BREAK = /\r?\n/;
const TOKEN_SPLIT = /[\s,;]+/;
const P_PREFIX = /^p?\s*[=<≤]?\s*/i;
const SPACES = /\s+/g;
const P_PREFIX_SPACES = /[ \t]*([=<≤])[ \t]*/g;

export interface ParsedP {
  entries: { label: string; p: number }[];
  /** Tokens or lines that were not a probability between 0 and 1. */
  invalid: number;
}

function toProbability(
  token: string,
  read: (text: string) => number | null
): number | null {
  const value = read(token.trim().replace(P_PREFIX, "").trim());
  return value !== null && value >= 0 && value <= 1 ? value : null;
}

/**
 * A plain list of p-values separated by commas, spaces or new lines. A leading "p =" or
 * "<" is ignored (a reported "p < .001" is taken as .001).
 */
export function parsePList(
  text: string,
  read: (text: string) => number | null
): ParsedP {
  const entries: ParsedP["entries"] = [];
  let invalid = 0;
  const tokens = text
    .replace(P_PREFIX_SPACES, "$1")
    .split(TOKEN_SPLIT)
    .filter((token) => token !== "");
  for (const token of tokens) {
    const value = toProbability(token, read);
    if (value === null) {
      invalid += 1;
    } else {
      entries.push({ label: "", p: value });
    }
  }
  return { entries, invalid };
}

/** One "label, p" per line: the p-value is the last field, the label is the rest. */
export function parseLabelled(
  text: string,
  read: (text: string) => number | null
): ParsedP {
  const entries: ParsedP["entries"] = [];
  let invalid = 0;
  for (const line of text.split(LINE_BREAK)) {
    const trimmed = line.trim();
    if (trimmed === "") {
      continue;
    }
    const cut = Math.max(
      trimmed.lastIndexOf(","),
      trimmed.lastIndexOf(";"),
      trimmed.lastIndexOf("\t"),
      trimmed.lastIndexOf(":")
    );
    const label =
      cut === -1 ? "" : trimmed.slice(0, cut).replace(SPACES, " ").trim();
    const value = toProbability(
      cut === -1 ? trimmed : trimmed.slice(cut + 1),
      read
    );
    if (value === null) {
      invalid += 1;
    } else {
      entries.push({ label, p: value });
    }
  }
  return { entries, invalid };
}
